import { useMemo, useEffect, useRef } from 'react';
import useFaceDetector from './useFaceDetector';
import useObjectDetector from './useObjectDetector';
import useFullscreenGuard from './useFullscreenGuard';
import useTabFocusGuard from './useTabFocusGuard';
import useViolationReporter from './useViolationReporter';

export default function useProctoring({
  webcamRef,
  cameraError,
  active,            
  disqualified,
  setDisqualified,
  maxViolations,
  isMobileDevice,
  requireFullscreen,
  violationEndpoint, 
  violationIdField,  
  violationIdValue,  
  onViolation,
}) {
  const { faceStatus, setFaceStatus } = useFaceDetector({ webcamRef, cameraError });

  const handleDisqualified = () => setDisqualified(true);

  const { violationCount, setViolationCount, registerViolation } = useViolationReporter({
    endpoint: violationEndpoint,
    idField: violationIdField,
    idValue: violationIdValue,
    active,
    disqualified,
    maxViolations,
    webcamRef,
    onDisqualified: handleDisqualified,
    onViolation,
  });

  const { objectDetectorReady } = useObjectDetector({
    webcamRef,
    cameraError,
    active,
    onPhoneDetected: () => registerViolation('phone_detected', 'Terdeteksi handphone selama tes berlangsung.'),
  });

  const { isFullscreen, enterFullscreen, fullscreenRequestRef } = useFullscreenGuard({
    requireFullscreen,
    active,
    disqualified,
    registerViolation,
  });

  useTabFocusGuard({ active, isMobileDevice, registerViolation, fullscreenRequestRef });

  const faceMissingStartRef = useRef(null); 

  useEffect(() => {
    if (!active || disqualified) {
      faceMissingStartRef.current = null;
      return undefined;
    }

    const interval = setInterval(() => {
      if (faceStatus === 'ok') {
        faceMissingStartRef.current = null;
        return;
      }

      if (faceStatus === 'face_multiple') {
        faceMissingStartRef.current = null;
        registerViolation('face_multiple', 'Terdeteksi lebih dari satu wajah di dalam kamera.');
        return;
      }

      if (faceStatus === 'detecting') return;
      if (faceStatus !== 'face_missing') return;

      if (faceMissingStartRef.current === null) {
        faceMissingStartRef.current = Date.now();
        return;
      }

      if (Date.now() - faceMissingStartRef.current >= 5000) {
        faceMissingStartRef.current = Date.now(); // reset timer setelah trigger
        registerViolation('face_missing', 'Wajah tidak terdeteksi lebih dari 5 detik.');
      }
    }, 1000);


    return () => clearInterval(interval);
  }, [active, disqualified, faceStatus, registerViolation]);

  return useMemo(() => ({
    faceStatus, setFaceStatus,
    objectDetectorReady,
    isFullscreen, enterFullscreen,
    violationCount, setViolationCount, registerViolation,
  }), [faceStatus, setFaceStatus, objectDetectorReady, isFullscreen, enterFullscreen, violationCount, setViolationCount, registerViolation]);
}